import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { resolveMarketAnalysisSegmentSellersByIds } from 'api/Atlas/marketAnalysisSegmentsTypeaheadApi'
import { AtlasUserOverride } from 'api/atlasClient'
import { MARKET_ANALYSIS_SEGMENTS, SELLER_NAME } from 'const/filters'
import { useUserOverrideContext } from 'context/UserOverrideContext'
import { FilterChipLabelOptions } from 'helpers/filters/dashboards'
import {
    FilterHierarchy,
    FilterOption,
    SingleClauseFilterValue,
    Widget,
} from 'types'

const MAS_DATA_SOURCE = 'market_analysis_segments'
const FIVE_MINUTES_MS = 5 * 60 * 1000

// Market Analysis Segment widgets persist the seller filter as `seller_id`
// values, but the widget header chip resolves labels via an `options` lookup.
// Fetch the matching `seller_name` rows so the chip can render names instead
// of raw IDs.
export function useMarketAnalysisSegmentChipLabels(
    widget: Widget,
    hierarchy: FilterHierarchy
): FilterChipLabelOptions {
    const isMas = widget.data_source === MAS_DATA_SOURCE

    const segmentIds = useMemo<string[]>(() => {
        if (!isMas) {
            return []
        }
        const segments = hierarchy[MARKET_ANALYSIS_SEGMENTS]?.values as
            | FilterOption[]
            | undefined
        return segments?.map((s) => String(s.value)) ?? []
    }, [isMas, hierarchy])

    const sellerIds = useMemo<string[]>(() => {
        if (!isMas) {
            return []
        }
        const clauses = hierarchy[SELLER_NAME]?.values as
            | SingleClauseFilterValue[]
            | undefined
        return clauses?.[0]?.value ?? []
    }, [isMas, hierarchy])

    const enabled = isMas && segmentIds.length > 0 && sellerIds.length > 0

    // On the print page the dashboard is rendered as its owner; resolving chip
    // labels through Atlas needs the same impersonation override the widget data
    // path uses, otherwise the Atlas token is minted for the automation user and
    // the request 403s. Undefined off the print page → default identity path.
    const { userOverride } = useUserOverrideContext()
    const override = useMemo<AtlasUserOverride | undefined>(
        () =>
            userOverride
                ? {
                      userId: userOverride.userId,
                      orgId: userOverride.organizationId,
                      orgGroupId: userOverride.organizationGroupId,
                  }
                : undefined,
        [userOverride]
    )

    const { data } = useQuery({
        queryKey: [
            'marketAnalysisSegments',
            'sellerChipLabels',
            segmentIds.join(','),
            sellerIds.join(','),
            override?.userId ?? null,
            override?.orgId ?? null,
            override?.orgGroupId ?? null,
        ],
        queryFn: () =>
            resolveMarketAnalysisSegmentSellersByIds(
                segmentIds,
                sellerIds,
                override
            ),
        enabled,
        staleTime: FIVE_MINUTES_MS,
    })

    return useMemo<FilterChipLabelOptions>(() => {
        const rows = data?.data.results ?? []
        if (rows.length === 0) {
            return {}
        }
        return {
            [SELLER_NAME]: rows.map((row) => ({
                label: row.seller_name,
                value: row.seller_id,
            })),
        }
    }, [data])
}
